/**
 * DISCRETE voxel emitters — spheres, cylinders and tapered capsules written straight onto the lattice,
 * with the pack's surface-weight convention baked in.
 *
 * The counterpart to `drawing.rasterizePartsAtScale`, which does the same job from continuous SDFs.
 * Both exist for a reason: an SDF part is exact and rotatable and knows nothing about noise, while
 * these carry a per-instance IRREGULARITY — the thing that makes a tree's canopy and a rock look
 * grown rather than manufactured — and are cheap enough to run per voxel of a model.
 *
 * `voxel/` rather than `terrain/` because nothing here knows what a stamp is: they are lattice
 * operations on the same `VoxelSample` record every other voxel pipeline speaks.
 *
 * EVERY EMITTER HERE WRITES THE BAND, not just the solid inside it. Keeping only the samples the shape
 * contains leaves the air outside saturated at -0.5, so every crossing is pulled toward the solid and
 * the model draws SMALLER than it was authored: measured on a radius-8 sphere, 7.73 of 8, a 6.7 cm
 * shortfall on every tree and rock the player stands next to. See voxel/surfaceBand.ts — this is the
 * same fix `rasterizePartsAtScale` needed, in the other emitter.
 */
import { minTubeRadiusFine } from './surfaceBand.js';
import { sdfEllipsoidS } from './shapes.js';
/**
 * Create a cylinder of voxels (used for tree trunks)
 */
export function cylinder(centerX, centerZ, radius, yStart, yEnd, material) {
    const voxels = [];
    // ONE VOXEL PAST THE SURFACE, for the band (see the module header). The sides are the only free
    // faces a trunk has — its top meets a canopy and its base is in the ground — so the Y range is
    // left alone.
    const r = Math.ceil(radius) + 1;
    for (let y = yStart; y < yEnd; y++) {
        for (let x = -r; x <= r; x++) {
            for (let z = -r; z <= r; z++) {
                const edgeDist = radius - Math.sqrt(x * x + z * z);
                const weight = Math.max(-0.5, Math.min(0.5, edgeDist * 0.5));
                if (weight <= -0.5)
                    continue; // beyond the band: nothing to say
                voxels.push({
                    x: centerX + x,
                    y,
                    z: centerZ + z,
                    material,
                    weight,
                });
            }
        }
    }
    return voxels;
}
/** Lower = smoother blobs. */
const SMOOTH_NOISE_FREQ = 0.3;
/**
 * The three sines of the SMOOTH noise, as (seed factor, x, y, z coefficients, amplitude) — the same
 * terms `sphere` always summed, written out so they can be tabulated per axis (see `sphere`).
 */
const SMOOTH_TERMS = [
    [1.1, 1.7, 2.3, 1.9, 1.0],
    [2.3, 2.1, 1.7, -1.3, 0.5],
    [0.7, -1.1, 1.9, 2.7, 0.3],
];
/**
 * Per-voxel GRIT: an integer hash of the lattice position and seed, to a fraction in [0, 1). This
 * replaced `sin(seed + 12.9898x + 78.233y + 37.719z) * 43758.5453` mod 1 — the shader-toy hash, whose
 * single `sin` is ~35 ns on V8 against ~2 ns for the multiply-xorshift below. Rocks are re-gritted by
 * this (a different but equally uncorrelated chipping); the checksum guards re-baseline once.
 */
function gritHash(x, y, z, seed) {
    let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(z | 0, 0x9e3779b1)
        ^ Math.imul((seed * 1000) | 0, 0x85ebca6b)) >>> 0;
    h ^= h >>> 15;
    h = Math.imul(h, 0x2c1b3c6d) >>> 0;
    h ^= h >>> 12;
    h = Math.imul(h, 0x297a2d39) >>> 0;
    h ^= h >>> 15;
    return (h >>> 8) / 16777216;
}
/**
 * A sphere of voxels, with a per-instance irregularity — tree canopies and rocks.
 *
 * `yScale` SQUASHES THE SHAPE INSIDE A CUBE THAT DOES NOT FOLLOW IT, and that is load-bearing rather
 * than sloppy: the loop spans ±ceil(radius) on every axis, so a rock with yScale > 1 is CLIPPED flat
 * top and bottom instead of growing taller. That flat cap is the rock's silhouette as shipped.
 *
 * COST, and where it went. This walks (2r+3)³ lattice points per blob, and the big oak draws ~500 of
 * them per variant; the noise was three `Math.sin` per point (~90 ns) and the distance a `sqrt`.
 * Three things here, none of which change what a plain (irregularity 0) sphere emits:
 *  - THE CORNERS ARE SKIPPED before any noise: the noise is bounded by ±irregularity, so a point
 *    further out than `radius + irregularity + band` cannot be in the band whatever the noise says.
 *  - THE CORE IS SKIPPED TOO: a point deeper than `radius - irregularity - band` clamps to +0.5
 *    whatever the noise says, so it is emitted at 0.5 with no noise and no sqrt.
 *  - THE SMOOTH NOISE IS TABULATED. Each term is sin(A + a·x + b·y + c·z); with per-axis sin/cos
 *    tables that is two angle additions — six multiplies — instead of a transcendental. The values
 *    differ from the direct sines only by float rounding, which the checksum guards absorb once.
 */
export function sphere(centerX, centerY, centerZ, radius, material, irregularity = 0, seed = 0, opts = {}) {
    const { yScale = 1, noise = 'smooth', ramp = 0.5 } = opts;
    const voxels = [];
    const r = Math.ceil(radius);
    // ...and one voxel past it for the band. The SOLID set stays inside `r` exactly as it always was:
    // `effectiveRadius` can exceed `radius` where the irregularity bulges outward, so walking wider
    // would grow the shape as well as its band, and every tree and rock in the world would change.
    const rOut = r + 1;
    const span = 2 * rOut + 1;
    const irr = Math.abs(irregularity);
    const band = 0.5 / ramp;
    const reach = radius + irr + band;
    const reachSq = reach * reach;
    const core = radius - irr - band;
    const coreSq = core > 0 ? core * core : -1;
    const noisy = irregularity !== 0;
    const smooth = noisy && noise !== 'grit';
    // Per-axis tables for the smooth terms: for term k, Sx[k][i] = sin(A_k + a_k·F·x), Cx likewise;
    // Sy/Cy over sy = y / yScale; Sz/Cz over z. Index i = coordinate + rOut.
    let Sx = null, Cx = null;
    let Sy = null, Cy = null;
    let Sz = null, Cz = null;
    if (smooth) {
        Sx = new Float64Array(3 * span);
        Cx = new Float64Array(3 * span);
        Sy = new Float64Array(3 * span);
        Cy = new Float64Array(3 * span);
        Sz = new Float64Array(3 * span);
        Cz = new Float64Array(3 * span);
        for (let k = 0; k < 3; k++) {
            const [sf, a, b, c] = SMOOTH_TERMS[k];
            const A = seed * sf;
            for (let i = 0; i < span; i++) {
                const v = i - rOut;
                const ax = A + a * SMOOTH_NOISE_FREQ * v;
                Sx[k * span + i] = Math.sin(ax);
                Cx[k * span + i] = Math.cos(ax);
                const ay = b * SMOOTH_NOISE_FREQ * (v / yScale);
                Sy[k * span + i] = Math.sin(ay);
                Cy[k * span + i] = Math.cos(ay);
                const az = c * SMOOTH_NOISE_FREQ * v;
                Sz[k * span + i] = Math.sin(az);
                Cz[k * span + i] = Math.cos(az);
            }
        }
    }
    const irrOver = irregularity / 1.8; // (n1 + n2 + n3) / 1.8 normalizes to roughly -1..1
    for (let y = -rOut; y <= rOut; y++) {
        const sy = y / yScale;
        const sySq = sy * sy;
        const iy = y + rOut;
        const yIn = y >= -r && y <= r;
        for (let x = -rOut; x <= rOut; x++) {
            const xIn = yIn && x >= -r && x <= r;
            const xySq = x * x + sySq;
            if (xySq >= reachSq)
                continue; // the whole z-row is beyond the band
            const ix = x + rOut;
            // Angle addition for the x and y terms of each sine, once per z-row.
            let s0 = 0, c0 = 0, s1 = 0, c1 = 0, s2 = 0, c2 = 0;
            if (smooth) {
                const SX = Sx, CX = Cx, SY = Sy, CY = Cy;
                s0 = SX[ix] * CY[iy] + CX[ix] * SY[iy];
                c0 = CX[ix] * CY[iy] - SX[ix] * SY[iy];
                s1 = SX[span + ix] * CY[span + iy] + CX[span + ix] * SY[span + iy];
                c1 = CX[span + ix] * CY[span + iy] - SX[span + ix] * SY[span + iy];
                s2 = SX[2 * span + ix] * CY[2 * span + iy] + CX[2 * span + ix] * SY[2 * span + iy];
                c2 = CX[2 * span + ix] * CY[2 * span + iy] - SX[2 * span + ix] * SY[2 * span + iy];
            }
            for (let z = -rOut; z <= rOut; z++) {
                const distSq = xySq + z * z;
                if (distSq >= reachSq)
                    continue; // beyond the band whatever the noise says
                const inBox = xIn && z >= -r && z <= r;
                if (distSq <= coreSq) {
                    // Deep inside: the ramp saturates whatever the noise says. (Always inBox: core < radius.)
                    voxels.push({ x: centerX + x, y: centerY + y, z: centerZ + z, material, weight: 0.5 });
                    continue;
                }
                let variation = 0;
                if (smooth) {
                    const iz = z + rOut;
                    const SZ = Sz, CZ = Cz;
                    const n1 = s0 * CZ[iz] + c0 * SZ[iz];
                    const n2 = (s1 * CZ[span + iz] + c1 * SZ[span + iz]) * 0.5;
                    const n3 = (s2 * CZ[2 * span + iz] + c2 * SZ[2 * span + iz]) * 0.3;
                    variation = (n1 + n2 + n3) * irrOver;
                }
                else if (noisy) {
                    variation = gritHash(x, y, z, seed) * (2 * irregularity) - irregularity;
                }
                const effectiveRadius = radius + variation;
                const dist = Math.sqrt(distSq);
                const edgeDist = effectiveRadius - dist;
                const weight = Math.max(-0.5, Math.min(0.5, edgeDist * ramp));
                if (weight <= -0.5)
                    continue; // beyond the band
                if (weight >= 0 && !inBox)
                    continue; // a solid the original walk never reached
                voxels.push({
                    x: centerX + x,
                    y: centerY + y,
                    z: centerZ + z,
                    material,
                    weight,
                });
            }
        }
    }
    return voxels;
}
/**
 * A TAPERED CAPSULE between two arbitrary points — the emitter a branch needs and the build system
 * cannot supply.
 *
 * `BuildShape.CYLINDER`/`CONE` are Y-AXIS shapes: `sdfFromConfig` measures them about the part's own
 * vertical, and a `BuildPart` carries a YAW and nothing else. A limb leaving a trunk at 47° off
 * vertical has no primitive to be, and giving the build system a capsule would mean a new shape in the
 * SDF evaluator, in the build UI's shape list, in its snap points and in its size semantics — all so
 * that one terrain model can draw a stick. So the stick is drawn here, discretely, beside the sphere
 * and the cylinder every other tree is already made of.
 *
 * Radius is lerped r0 -> r1 along the segment, which is what makes a branch a branch: a limb narrows
 * into the fork it feeds, and a chain of segments reads as one tapering arm rather than a stack of
 * pipes.
 *
 * SAME BAND CONVENTION AS `cylinder`: the walk runs one voxel past the widest radius and every sample
 * inside that shell carries its true distance to the surface, so SurfaceNets has a crossing to
 * interpolate instead of a staircase. See the module header and voxel/surfaceBand.ts.
 *
 * RADII ARE FLOORED AT `minTubeRadiusFine(1)` HERE, in the emitter, and that is a deliberate exception
 * to surfaceBand's "never silently in the shape evaluator". The exception is safe because of what
 * this function is: a FINE-lattice emitter for round bodies drawn along arbitrary axes, with no
 * coarse-level caller and no caller that means a sub-lattice tube (the sub-voxel column that is right
 * at lod 3 is `pinPart`, on the other path). A tube under the floor is surface samples with no
 * connected body between them, and the first model to use this emitter derived its floor from the
 * WALL bound by hand — the conversion now lives in surfaceBand and is applied at the one point every
 * branch passes through. The caller is still expected to author ABOVE the floor (treeOakLarge's
 * LAYER_R sits on it only at its last, twig layer), so the clamp only ever bites on an error.
 */
export function taperedSegment(x0, y0, z0, x1, y1, z1, r0, r1, material) {
    const floor = minTubeRadiusFine(1);
    if (r0 < floor)
        r0 = floor;
    if (r1 < floor)
        r1 = floor;
    const voxels = [];
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
    const lenSq = dx * dx + dy * dy + dz * dz;
    const rMax = r0 > r1 ? r0 : r1;
    // The widest radius plus the one voxel of band: nothing further from the segment than this emits.
    const pad = Math.ceil(rMax) + 1;
    // THE WALK FOLLOWS THE SEGMENT, slab by slab along its dominant axis, rather than filling its
    // bounding cube: a 30-voxel diagonal limb has a 27k-point cube for ~1k emitted voxels. For a slab at
    // coordinate `s` the emitted points lie within `pad / cos θ` of the segment's point at that slab
    // (θ the segment's angle to the axis; cos θ ≥ 1/√3 for the dominant axis), and within `pad` of an
    // endpoint beyond the ends — so a square of half-width ceil(pad·√3) around the axis point covers
    // them all. The per-point test is unchanged, so the emitted set is exactly the cube walk's.
    const adx = Math.abs(dx), ady = Math.abs(dy), adz = Math.abs(dz);
    const axis = adx >= ady && adx >= adz ? 0 : ady >= adz ? 1 : 2;
    const p0 = [x0, y0, z0], p1 = [x1, y1, z1];
    const uAxis = axis === 0 ? 1 : 0, vAxis = axis === 2 ? 1 : 2;
    const sA = p0[axis], sB = p1[axis];
    const sLo = Math.floor(Math.min(sA, sB)) - pad, sHi = Math.ceil(Math.max(sA, sB)) + pad;
    const side = Math.ceil(pad * 1.7320508075688772);
    const sLen = sB - sA;
    const pos = [0, 0, 0];
    for (let sv = sLo; sv <= sHi; sv++) {
        const t = sLen !== 0 ? Math.max(0, Math.min(1, (sv - sA) / sLen)) : 0;
        const cu = p0[uAxis] + (p1[uAxis] - p0[uAxis]) * t;
        const cv = p0[vAxis] + (p1[vAxis] - p0[vAxis]) * t;
        const uLo = Math.floor(cu) - side, uHi = Math.ceil(cu) + side;
        const vLo = Math.floor(cv) - side, vHi = Math.ceil(cv) + side;
        pos[axis] = sv;
        for (let u = uLo; u <= uHi; u++) {
            pos[uAxis] = u;
            for (let v = vLo; v <= vHi; v++) {
                pos[vAxis] = v;
                const x = pos[0], y = pos[1], z = pos[2];
                // Closest point on the SEGMENT (not the infinite line): the projection parameter, clamped, so
                // both ends round off into hemispherical caps rather than shearing flat.
                const px = x - x0, py = y - y0, pz = z - z0;
                const tt = lenSq > 0
                    ? Math.max(0, Math.min(1, (px * dx + py * dy + pz * dz) / lenSq))
                    : 0;
                const cx = px - dx * tt, cy = py - dy * tt, cz = pz - dz * tt;
                const dist = Math.sqrt(cx * cx + cy * cy + cz * cz);
                const edgeDist = (r0 + (r1 - r0) * tt) - dist;
                const weight = Math.max(-0.5, Math.min(0.5, edgeDist * 0.5));
                if (weight <= -0.5)
                    continue; // beyond the band: nothing to say
                voxels.push({ x, y, z, material, weight });
            }
        }
    }
    return voxels;
}
/**
 * AN ORIENTED OBLATE ELLIPSOID — a leaf pad: footprint radius `a` about a short axis (`nx`, `ny`,
 * `nz`, unit) of half-thickness `c`. The big oak hangs one on the ends of each branch group, its
 * short axis mostly up and tilted toward the crown's surface, which is what a layer of oak foliage
 * is; `sphere` with `yScale` cannot tilt, and the SDF pipeline's per-voxel part scan is the wrong
 * shape of cost for eighty pads over a 30 m crown.
 *
 * COST. Walks the pad's own TIGHT box — per world axis, the extent of the rotated ellipsoid is
 * √Σ(axis component × semi-axis)², so a 3 × 3 × 1 m pad tilted 30° is ~26 × 12 × 26 voxels rather
 * than the 27³ of its bounding cube — and each sample is three dot products and the scalar
 * `sdfEllipsoidS`. No noise: overlapping tilted pads are lumpy enough on their own.
 *
 * FLOORED. A slab's half-thickness is the same bound as a tube's radius (half the wall's minimum
 * thickness — surfaceBand.minTubeRadiusFine), and it is clamped here, where every pad passes.
 */
export function orientedEllipsoid(cx, cy, cz, nx, ny, nz, a, c, material) {
    const floor = minTubeRadiusFine(1);
    if (c < floor)
        c = floor;
    if (a < c)
        a = c;
    // The frame: n is the short axis; u any perpendicular; v = n × u.
    let ux, uy, uz;
    if (Math.abs(ny) < 0.9) {
        ux = -nz;
        uy = 0;
        uz = nx;
    }
    else {
        ux = 1;
        uy = 0;
        uz = 0;
    }
    {
        const d = ux * nx + uy * ny + uz * nz;
        ux -= d * nx;
        uy -= d * ny;
        uz -= d * nz;
        const l = Math.sqrt(ux * ux + uy * uy + uz * uz) || 1;
        ux /= l;
        uy /= l;
        uz /= l;
    }
    const vx = ny * uz - nz * uy, vy = nz * ux - nx * uz, vz = nx * uy - ny * ux;
    // Extents, plus one voxel for the band (see the module header).
    const ex = Math.sqrt((ux * a) ** 2 + (nx * c) ** 2 + (vx * a) ** 2) + 1;
    const ey = Math.sqrt((uy * a) ** 2 + (ny * c) ** 2 + (vy * a) ** 2) + 1;
    const ez = Math.sqrt((uz * a) ** 2 + (nz * c) ** 2 + (vz * a) ** 2) + 1;
    const x0 = Math.floor(cx - ex), x1 = Math.ceil(cx + ex);
    const y0 = Math.floor(cy - ey), y1 = Math.ceil(cy + ey);
    const z0 = Math.floor(cz - ez), z1 = Math.ceil(cz + ez);
    const voxels = [];
    for (let y = y0; y <= y1; y++) {
        const dy = y - cy;
        for (let z = z0; z <= z1; z++) {
            const dz = z - cz;
            for (let x = x0; x <= x1; x++) {
                const dx = x - cx;
                const pu = dx * ux + dy * uy + dz * uz;
                const pn = dx * nx + dy * ny + dz * nz;
                const pv = dx * vx + dy * vy + dz * vz;
                const edgeDist = -sdfEllipsoidS(pu, pn, pv, a, c, a);
                const weight = Math.max(-0.5, Math.min(0.5, edgeDist * 0.5));
                if (weight <= -0.5)
                    continue; // beyond the band
                voxels.push({ x, y, z, material, weight });
            }
        }
    }
    return voxels;
}
//# sourceMappingURL=blobs.js.map